import { useState, useEffect, FunctionComponent, useContext } from "react";
import ShortAppContainer from "./short-app-container";
import "react-responsive-carousel/lib/styles/carousel.css";
import { navigate, Link, useStaticQuery, graphql } from "gatsby";
import { FindAStoreContext, FindAStoreProvider } from "../contexts/findAStore";
import { Icon } from "../utils/Icons";
import StoreDetails from "../../../site/src/cobra-find-a-store/components/store-details";
import { Brands, Store } from "../types.d";
import FindAStoreLayout from "../layouts/FindAStoreLayout";
import { WithContext, LocalBusiness } from "schema-dts";

interface Props {
  pageContext?: {
    store?: Store;
    baseUrl?: string;
    brands: Brands[];
    mapCenter: { lat: number; lng: number };
    mapZoom?: number;
  };
  store: Store;
  isOpen: boolean;
  brands: string[];
}

type Reviews = {
  reviews: {
    score: {
      stars: number;
    };
    numberOfReviews: {
      total: number;
    };
  };
};
const convertHoursToSchema = (input: string) => {
  const dayMap = {
    Mon: "Monday",
    Tue: "Tuesday",
    Wed: "Wednesday",
    Thu: "Thursday",
    Fri: "Friday",
    Sat: "Saturday",
    Sun: "Sunday",
  };

  const result = [];

  const parts = input.split(";").map((part) => part.trim());

  for (const part of parts) {
    const [daysRaw, hoursRaw] = part.split(":").map((p) => p.trim());

    let daysRange = [];

    if (daysRaw.includes("-")) {
      const [start, end] = daysRaw.split("-").map((d) => d.trim());
      const dayKeys = Object.keys(dayMap);
      const startIndex = dayKeys.indexOf(start);
      const endIndex = dayKeys.indexOf(end);
      daysRange = dayKeys.slice(startIndex, endIndex + 1);
    } else {
      daysRange = daysRaw.split(",").map((d) => d.trim());
    }

    if (/closed/i.test(hoursRaw)) {
      // If closed, skip the opens/closes, only return dayOfWeek
      for (const day of daysRange) {
        result.push({
          "@type": "OpeningHoursSpecification",
          dayOfWeek: dayMap[day],
          // No opens/closes means closed for the day
        });
      }
    } else {
      const [opensRaw, closesRaw] = hoursRaw
        .split("-")
        .map((str) => str.trim());
      const opens = convertTo24Hour(opensRaw);
      const closes = convertTo24Hour(closesRaw);

      for (const day of daysRange) {
        result.push({
          "@type": "OpeningHoursSpecification",
          dayOfWeek: dayMap[day],
          opens,
          closes,
        });
      }
    }
  }

  return result;
};

function convertTo24Hour(timeStr: string) {
  const match = timeStr.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
  if (!match) return null;

  let [_, hour, minutes, meridian] = match;
  hour = parseInt(hour);
  minutes = minutes || "00";

  if (meridian.toLowerCase() === "pm" && hour !== 12) hour += 12;
  if (meridian.toLowerCase() === "am" && hour === 12) hour = 0;

  return `${String(hour).padStart(2, "0")}:${minutes}`;
}
export interface Review {
  rating: number;
}
const buildJsonLD = (
  store: (Store & Reviews) | undefined,
  baseUrl: string
): WithContext<LocalBusiness> | undefined => {
  // console.log(store ? convertHoursToSchema(store.hours) : null);
  if (!store) return store;
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `Cash Money | ${store.crossStreets}`,
    alternateName: "Cash Money Loans",
    description:
      "Cash Money provides fast, flexible loans, including Payday Loans and Lines of Credit, designed to help you manage unexpected expenses and stay financially on track.",
    priceRange: "$-$$$$",
    image: {
      "@type": "ImageObject",
      url: "https://image.e.cashmoney.ca/lib/fe2411747364047b731d77/m/1/cc9794ce-7e88-4df5-9d4c-b29aaaa94c7a.png",
      contentUrl:
        "https://image.e.cashmoney.ca/lib/fe2411747364047b731d77/m/1/cc9794ce-7e88-4df5-9d4c-b29aaaa94c7a.png",
      name: "Cash Money",
    },
    url: `${baseUrl}/find-a-store/${store.city?.state?.urlSafeName}/${store.city?.urlSafeName}/${store.url}/`,
    hasMap:
      "https://maps.google.com?saddr=Current+Location&daddr=@" + store.gPS,
    telephone: store.phone ?? "",
    faxNumber: store.fax ?? "",
    geo: {
      "@type": "GeoCoordinates",
      latitude: store.gPS?.split(",")[0] ?? "",
      longitude: store.gPS?.split(",")[1] ?? "",
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: `${store.address1}${
        store.address2 ? ` ${store.address2}` : ""
      }`,
      addressLocality: store.city?.name ?? "",
      addressRegion: store.city?.state?.name ?? "",
      postalCode: store.zip ?? "",
    },
    areaServed: {
      "@type": "State",
      name: store.city?.state?.name ?? "",
      additionalProperty: [
        {
          "@type": "PropertyValue",
          name: "Service Areas",
          value: "",
        },
      ],
    },
    sameAs: [
      "https://www.facebook.com/CashMoney",
      "https://www.instagram.com/cash_money1992/",
      "https://www.linkedin.com/company/cash-money/",
      `https://www.trustpilot.com/review/cashmoney.ca`,
    ],

    // @ts-ignore
    openingHoursSpecification: store.hours && convertHoursToSchema(store.hours),
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Line of Credit Loans",
      url: "https://www.cashmoney.ca/line-of-credit/",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Cash Advances",
            url: "https://www.cashmoney.ca/cash-advances/",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Payday Loans",
            url: "https://www.cashmoney.ca/payday-loans/",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Auto Equity Loans",
            url: "https://www.cashmoney.ca/auto-equity-loans/",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Personal Loans",
            url: "https://www.cashmoney.ca/personal-loans/",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Instant Payday Loans",
            url: "https://www.cashmoney.ca/payday-loans/instant/",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Online Payday Loans",
            url: "https://www.cashmoney.ca/payday-loans/online-loans/",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Online Loans",
            url: "https://www.cashmoney.ca/online-loans/",
          },
        },
      ],
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: store?.reviews?.score?.stars ?? 0,
      reviewCount: store.reviews.numberOfReviews.total,
      bestRating: "5",
      worstRating: "1",
    },
  };
};
export function Head({
  pageContext,
}: {
  pageContext: { store: Store; baseUrl: string };
}) {
  const { store, baseUrl } = pageContext;
  const { title, metaDescription } = store;
  const reviews = useStaticQuery(graphql`
    query {
      allTrustPilotReviewTotal {
        edges {
          node {
            score {
              stars
              trustScore
            }
            displayName
            numberOfReviews {
              total
              usedForTrustScoreCalculation
            }
          }
        }
      }
    }
  `);
  const { allTrustPilotReviewTotal } = reviews;
  const addedReviewData = {
    ...store,
    reviews: allTrustPilotReviewTotal.edges[0].node,
  };
  return (
    <>
      <title>{title}</title>
      <meta name="description" content={metaDescription ?? ""} />
      <script type="application/ld+json">
        {store.name === "Cash Money" &&
          JSON.stringify(buildJsonLD(addedReviewData, baseUrl))}
      </script>
    </>
  );
}

const StoreDetailContainer: FunctionComponent<Props> = (props: Props) => {
  const {
    state: { activeStore, place },
    dispatch,
  } = useContext(FindAStoreContext);
  const setActiveStore = (id: number | undefined) =>
    dispatch({ type: "SetActiveStore", payload: id });

  const [store] = useState<Store | undefined>(props.pageContext?.store);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (activeStore !== store?.id) {
      setActiveStore(store?.id);
    }
  }, []);

  useEffect(() => {
    if (
      place ||
      (store && activeStore && activeStore !== store.id) ||
      (store?.disabledAt && new Date(store?.disabledAt) <= new Date())
    )
      navigate("/find-a-store/");
  }, [activeStore, place, store]);

  useEffect(() => {
    if (props.isOpen == false) {
      setIsOpen(false);
    }
  }, [props.isOpen]);
  if (!store) return null;
  return (
    <FindAStoreLayout hideMap={true} pageContext={props.pageContext}>
      <div
        id="cover"
        sx={{
          position: "absolute",
          mx: "auto",
          height: "100%",
          width: "100%",
          overflow: "auto",
          bg: "rgba(0,0,0,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        role="button"
        tabIndex={-1}
        onKeyDown={undefined}
        onClick={(e) => {
          if ((e.target as HTMLDivElement).id == "cover")
            navigate("/find-a-store/");
        }}
      >
        <div
          sx={{
            bg: "white",
            mr: "auto",
            ml: [0, null, null, "auto"],
            width: 600,
            height: "90vh",
            overflow: "auto",
          }}
        >
          {isOpen ? (
            <ShortAppContainer
              close={() => setIsOpen(false)}
              storeState={store.city?.state?.name ?? ""}
              storeId={props.pageContext?.store?.id || props.store?.id}
            />
          ) : (
            <div
              sx={{
                width: "100%",
                position: ["absolute", null, null, "relative"],
                top: 0,
                overflowY: ["scroll", null, null, "visible"],
                backgroundColor: "#fff",
                zIndex: 5,
                height: "100%",
                transition: "width 0.5s cubic-bezier(0, 1, 0, 1)",
                willChange: "transform",
              }}
            >
              <div
                sx={{
                  padding: "20px",
                  fontSize: "24px",
                  cursor: "pointer",
                  position: "absolute",
                  zIndex: 9,
                  right: 0,
                  "& svg": {
                    color: "#000",
                  },
                }}
              >
                <Link to="/find-a-store/">
                  <Icon
                    alt="Close Details"
                    name="times"
                    tabIndex={0}
                    width={24}
                    height={24}
                    fill="#000"
                  />
                </Link>
              </div>
              <StoreDetails
                store={store}
                brands={props.pageContext?.brands}
                _openApp={() => setIsOpen(true)}
                baseUrl={props.pageContext?.baseUrl ?? ""}
              />
            </div>
          )}
        </div>
      </div>
    </FindAStoreLayout>
  );
};

export default StoreDetailContainer;
